import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, Search } from "lucide-react";
import api from "../services/api";

import NieuweKlantModal from "../components/NieuweKlantModal";
import ClientExportModal from "../components/clientDetail/ClientExportModal";
import { exportClients } from "../utils/clientExport";
import CustomerExportActions from "../components/customerImport/CustomerExportActions";

export default function KlantenPage() {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [pageMessage, setPageMessage] = useState("");

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    const res = await api.get("/clients");
    setClients(Array.isArray(res.data) ? res.data : []);
  };

  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;

      const naam = String(client.naam || "").toLowerCase();
      const plaats = String(client.plaats || "").toLowerCase();
      const email = String(client.email || "").toLowerCase();

      return naam.includes(q) || plaats.includes(q) || email.includes(q);
    });
  }, [clients, search]);

  const selectedCount = useMemo(() => filteredClients.filter((item) => selectedIds.has(String(item._id))).length, [filteredClients, selectedIds]);

  const allFilteredSelected = filteredClients.length > 0 && selectedCount === filteredClients.length;

  const toggleClient = (clientId) => {
    const id = String(clientId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredClients.forEach((client) => next.delete(String(client._id)));
      } else {
        filteredClients.forEach((client) => next.add(String(client._id)));
      }
      return next;
    });
  };

  const handleExport = async ({ scope, format }) => {
    try {
      setExportLoading(true);
      setPageMessage("");

      const source = scope === "all"
        ? filteredClients
        : filteredClients.filter((client) => selectedIds.has(String(client._id)));
      const normalizedFormat = String(format || "").toLowerCase();

      // PDF/DOCX blijven lokaal voor nu; CSV/JSON gaan via backend tenant-safe export.
      if (normalizedFormat === "pdf" || normalizedFormat === "docx") {
        await exportClients({
          clients: source,
          format: normalizedFormat,
          filenameBase: `logboek24-klanten-${new Date().toISOString().slice(0, 10)}`,
        });
      } else {
        const params = new URLSearchParams();
        params.set("format", normalizedFormat || "csv");
        params.set("search", search || "");
        if (scope === "selected") {
          const ids = source.map((item) => String(item._id)).filter(Boolean);
          if (ids.length > 0) params.set("ids", ids.join(","));
        }

        const res = await api.get(`/clients/export?${params.toString()}`, {
          responseType: "blob",
        });

        const ext = normalizedFormat === "json" ? "json" : "csv";
        const blob = new Blob([res.data], {
          type: normalizedFormat === "json" ? "application/json" : "text/csv",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `klanten-export-${new Date().toISOString().slice(0, 10)}.${ext}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      }

      setShowExportModal(false);
      setPageMessage(`Export succesvol (${format.toUpperCase()}).`);
    } catch (err) {
      setPageMessage(err?.message || "Export mislukt.");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-4 md:p-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <header className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-slate-900 p-2 text-white">
                <Building2 size={18} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Logboek24</p>
                <h1 className="text-2xl font-semibold text-slate-900">Klanten</h1>
              </div>
            </div>

            <CustomerExportActions
              onImport={() => navigate("/klanten/import")}
              onExport={() => setShowExportModal(true)}
              onNew={() => setShowNew(true)}
            />
          </div>
        </header>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <label className="relative block">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Zoek op naam, plaats of e-mail..."
              className="w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-3 text-sm outline-none ring-slate-300 transition focus:ring-2"
            />
          </label>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={allFilteredSelected} onChange={toggleAllFiltered} />
              Alles selecteren (gefilterde lijst)
            </label>
            <span>{selectedCount} geselecteerd</span>
          </div>
          {pageMessage && <p className="mt-2 text-sm text-slate-600">{pageMessage}</p>}
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {filteredClients.map((client) => {
            const checked = selectedIds.has(String(client._id));
            return (
              <article key={client._id} className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-4 last:border-b-0">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleClient(client._id)}
                    onClick={(event) => event.stopPropagation()}
                  />

                  <button className="text-left" onClick={() => navigate(`/klanten/${client._id}`)}>
                    <h3 className="text-sm font-semibold text-slate-900">{client.naam}</h3>
                    <p className="text-sm text-slate-500">{client.plaats || "Onbekende locatie"}</p>
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                      client.facturatieStatus === "klaar"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {client.facturatieStatus || "onbekend"}
                  </span>
                  <button
                    type="button"
                    onClick={() => navigate(`/klanten/${client._id}`)}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Open klant-hub
                    <ArrowRight size={14} />
                  </button>
                </div>
              </article>
            );
          })}

          {filteredClients.length === 0 && (
            <div className="p-4 text-sm text-slate-500">Geen klanten gevonden.</div>
          )}
        </div>
      </div>

      {showNew && (
        <NieuweKlantModal
          onClose={() => setShowNew(false)}
          onSaved={() => {
            setShowNew(false);
            loadClients();
          }}
        />
      )}

      <ClientExportModal
        open={showExportModal}
        loading={exportLoading}
        selectedCount={selectedCount}
        totalCount={filteredClients.length}
        onClose={() => setShowExportModal(false)}
        onSubmit={handleExport}
      />
    </div>
  );
}
